import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import request from "../../utils/http";
import toaster from "../../utils/toaster";
import { Spinner } from "baseui/spinner";
import ActionHeader from "../../components/ActionHeader";
import { Button } from "baseui/button";
import { FormControl } from "baseui/form-control";
import { Overflow } from "baseui/icon";
import { StatefulPopover } from "baseui/popover";
import ActionBar from "../../components/ActionBar";
import { AiOutlineCheckCircle, AiOutlineLock } from "react-icons/ai";
import { Tabs, Tab } from "baseui/tabs-motion";
import enums from "../../utils/enums";
import { DateTime } from "luxon";
import { Tag } from "baseui/tag";
import CandidateInternalTasks from "./CandidateInternalTasks";
import Prose from "../../components/Prose";
import { StyledTable, StyledHeadCell, StyledBodyCell } from "baseui/table-grid";
import {
	dateFromDateArray,
	getDateString,
	getDayPart,
	getMonthPart,
	getRelativeDateString,
	getYearPart,
	zonelessIsoDate,
} from "../../utils/date";
import { FieldGrid } from "./FieldGrid";
import Copy from "../../components/Copy";
import { Input } from "baseui/input";
import { Radio, RadioGroup } from "baseui/radio";
import { Datepicker } from "baseui/datepicker";
import CandidateSites from "../../components/CandidateSites";
import { Select } from "baseui/select";

const Container = styled.div`
	label.required::after {
		content: " *";
		color: red;
`;

const Warning = styled.div`
	margin-top: 1rem;
	margin-bottom: 1rem;
	padding: 1rem;
	box-sizing: border-box;
	background-color: red;
	color: white;
	font-weight: bold;
`;

const DateContainer = styled.div`
	display: flex;
	.separator {
		margin-right: 1rem;
	}
`;

const CandidateZambion = () => {
	const navigate = useNavigate();
	const urlParams = useParams() as any;
	const [activeTab, setActiveTab] = React.useState<React.Key>(0);

	const [candidateId, setCandidateId] = useState("");

	const [data, setData] = useState(null as any);
	const [fields, setFields] = useState(null as any);

	const [loaded, setLoaded] = useState(false);

	const [candidateCode, setCandidateCode] = useState("");
	const [candidateTeam, setCandidateTeam] = useState("");
	const [candidateCostCentre, setCostCentre] = useState<any>([]);
	const [candidateDepartment, setDepartment] = useState<any>([]);
	const [candidateStartDate, setCandidateStartDate] = useState(null as any);

	const [allCostCentres, setAllCostCentres] = useState([] as any);
	const [allDepartments, setAllDepartments] = useState([] as any);

	const [visaCommentRestrictValue, setVisaCommentRestrictValue] = useState<any>(
		[]
	);

	type FieldErrors = {
		candidateCode: string;
		candidateCostCentre: string;
		visaCommentRestrictValue: string;
	};

	const initialFieldErrors: FieldErrors = {
		candidateCode: "",
		candidateCostCentre: "",
		visaCommentRestrictValue: "",
	};

	const clearFieldError = (fieldName: keyof FieldErrors) => {
		setFieldErrors((prevErrors) => {
			const updatedErrors = { ...prevErrors };
			delete updatedErrors[fieldName];
			return updatedErrors;
		});
	};

	const [fieldErrors, setFieldErrors] = useState(initialFieldErrors);

	const requiredFields = [
		"candidateCode",
		"candidateCostCentre",
		"visaCommentRestrictValue",
	];

	const validateForm = () => {
		const errors: FieldErrors = { ...initialFieldErrors };

		if (!candidateCode || candidateCode.length === 0) {
			errors.candidateCode = "Pin Number (Code) is required";
		} else {
			const regex = /^\d{4,}$/;
			if (!regex.test(candidateCode)) {
				errors.candidateCode =
					"Pin Number (Code) must be a positive integer with at least 4 digits.";
			} else {
				clearFieldError("candidateCode");
			}
		}

		if (!candidateCostCentre || candidateCostCentre.length === 0) {
			errors.candidateCostCentre = "Cost Centre is required";
		} else {
			clearFieldError("candidateCostCentre");
		}

		if (!visaCommentRestrictValue || visaCommentRestrictValue.length === 0) {
			errors.visaCommentRestrictValue = "Visa Comment/Restriction is required";
		} else {
			clearFieldError("visaCommentRestrictValue");
		}

		setFieldErrors(errors);

		const isValid = Object.values(errors).every((error) => error === "");

		return isValid;
	};

	const validateAndSave = async () => {
		const isFormValid = validateForm();

		if (isFormValid) {
			await sendToZambion();
		} else {
			toaster.error("Please fill in all required fields");
		}
	};

	const updateUrlParams = async () => {
		setCandidateId(urlParams.candidateId);
	};

	const getCandidate = async () => {
		let result = await request(
			"GET",
			`/api/admin/candidate-zambion/${candidateId}`,
			null,
			true,
			false
		);

		if (result.success && result.data?.fields) {
			setData(result.data);
			setFields(result.data.fields);
			if(result.data.fields["CandidateInternal.Department"] != null)
			{
				setDepartment([
					{
						value: result.data.fields["CandidateInternal.Department"],
						label: result.data.fields["CandidateInternal.DepartmentName"],
					},
				]);
			}

			let savedStartDate = result.data.fields["CandidateAddendum.StartDate"];

			if (savedStartDate != null) {
				setCandidateStartDate(new Date(savedStartDate));
			}
			setLoaded(true);
		} else {
			toaster.error("Unable to access candidate");
		}
	};

	const sendToZambion = async () => {
		setLoaded(false);

		let result = await request(
			"POST",
			`/api/admin/candidate-zambion/${candidateId}`,
			{
				code: candidateCode,
				team: candidateTeam,
				department:
					candidateDepartment.length == 1
						? candidateDepartment[0]?.value
						: null,
				costCentre:
					candidateCostCentre.length == 1
						? candidateCostCentre[0]?.value
						: null,
				requestAdditionalHoursAgreement: true,// Always included for now. TODO: add this to the UI
				visaComments:
					visaCommentRestrictValue.length == 1
						? visaCommentRestrictValue[0].value
						: null,
				// iwi : "",//TODO: add this to the UI
				startDate: zonelessIsoDate(dateFromDateArray(candidateStartDate)),
			},
			true,
			false
		);

		if (result.success && result.data?.fields) {
			setData(result.data);
			setFields(result.data.fields);
			setLoaded(true);

			toaster.success("Candidate saved successfully in Zambion");
		} else {
			toaster.error("Unable to send candidate to Zambion");
		}
	};

	const getZambionCostCentres = async () => {
		let result = await request(
			"GET",
			"/api/admin/candidate-zambion/cost-centres",
			null,
			true,
			false
		);
		if (result.success && result.data?.costCentres) {
			setAllCostCentres(result.data.costCentres);
		} else {
			toaster.error("Unable to access Cost Centres");
		}
	};

	const getZambionDepartments = async () => {
		let result = await request(
			"GET",
			"/api/admin/candidate-zambion/departments",
			null,
			true,
			false
		);
		if (result.success && result.data?.departments) {
			setAllDepartments(result.data.departments);
		} else {
			toaster.error("Unable to access departments");
		}
	};

	useEffect(() => {
		if (candidateId != "") {
			getCandidate();
		}
	}, [candidateId]);

	useEffect(() => {
		updateUrlParams();
	}, [urlParams, updateUrlParams, getZambionDepartments]);

	useEffect(() => {
		getZambionCostCentres();
	}, []);

	useEffect(() => {
		getZambionDepartments();
	}, []);

	const enableManualMode = () => {
		setData({
			...data,
			inZambion: true,
		});
	};

	return (
		<Container>
			{!loaded && (
				<div>
					<Spinner />
				</div>
			)}
			{loaded && fields && data && (
				<>
					<ActionHeader>
						<div>
							<h1>
								Zambion:{" "}
								{data.inZambion &&
									fields["CandidateInternal.CandidateCode"] && (
										<>{fields["CandidateInternal.CandidateCode"]}: </>
									)}{" "}
								{fields["Candidate.FirstName"]} {fields["Candidate.LastName"]}
							</h1>
						</div>
						<div>
							<Button kind="secondary" onClick={enableManualMode}>
								Manual Mode
							</Button>
							&nbsp;&nbsp;
							<Button
								kind="secondary"
								onClick={() => {
									navigate(`/candidate/${candidateId}`);
								}}
							>
								Back to Candidate
							</Button>
						</div>
					</ActionHeader>
					<Tabs
						activeKey={activeTab}
						onChange={({ activeKey }) => setActiveTab(activeKey)}
					>
						<Tab title="Main">
							{!data.inZambion ? (
								<Container>
									{!data.requiredFieldsComplete && (
										<Warning>
											Note: Some required fields appear to be missing. This
											application may not be complete, or may need to be
											manually added to Zambion. <br />
											<br />
											<Button onClick={enableManualMode}>
												Enable manual mode
											</Button>
										</Warning>
									)}

									<FieldGrid>
										<div>
											<FormControl label={"First name"}>
												<Copy>{fields["Candidate.FirstName"]}</Copy>
											</FormControl>
										</div>
										<div>
											<FormControl label={"Last name"}>
												<Copy>{fields["Candidate.LastName"]}</Copy>
											</FormControl>
										</div>
										<div>
											<FormControl
												label={
													<label
														className={
															requiredFields.includes("candidateCode")
																? "required"
																: ""
														}
													>
														PIN Number (Code)
													</label>
												}
												error={fieldErrors.candidateCode}
											>
												<>
													<Input
														value={candidateCode}
														onChange={(e) => {
															setCandidateCode(e.currentTarget.value);
														}}
													/>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl
												label={
													<label
														className={
															requiredFields.includes(
																"visaCommentRestrictValue"
															)
																? "required"
																: ""
														}
													>
														Visa Comment/Restriction
													</label>
												}
												error={fieldErrors.visaCommentRestrictValue}
											>
												<>
													<Select
														labelKey={"label"}
														valueKey={"value"}
														maxDropdownHeight="12rem"
														clearable={false}
														options={enums.VisaCommentRestriction}
														value={visaCommentRestrictValue}
														onChange={(params) => {
															setVisaCommentRestrictValue(params.value);
														}}
													/>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl label={"Department"}>
												<>
													<Select
														required
														options={allDepartments.map((department: any) => {
															return {
																value: department.id,
																label:
																	department.region.name +
																	" - " +
																	department.name,
															};
														})}
														labelKey={"label"}
														valueKey={"value"}
														value={candidateDepartment}
														clearable={false}
														maxDropdownHeight="12rem"
														onChange={(params) => {
															setDepartment(params.value);
														}}
													/>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl
												label={
													<label
														className={
															requiredFields.includes("candidateCostCentre")
																? "required"
																: ""
														}
													>
														Cost Centre
													</label>
												}
												error={fieldErrors.candidateCostCentre}
											>
												<>
													<Select
														options={allCostCentres.map((costCentre: any) => {
															return {
																value: costCentre.id,
																label:
																	costCentre.name + " - " + costCentre.code,
															};
														})}
														labelKey={"label"}
														valueKey={"value"}
														value={candidateCostCentre}
														clearable={false}
														maxDropdownHeight="12rem"
														onChange={(params) => {
															setCostCentre(params.value);
														}}
														required
													/>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl label={"Confirmed Start Date"}>
												<>
													<Datepicker
														onChange={({ date }) =>
															setCandidateStartDate(
																Array.isArray(date) ? date : [date]
															)
														}
														value={candidateStartDate}
														formatString="d MMMM yyyy"
														placeholder=" "
														mask={null}
													/>
												</>
											</FormControl>
										</div>
									</FieldGrid>
									<div>
										<Button onClick={validateAndSave}>
											Send Candidate to Zambion
										</Button>
									</div>
								</Container>
							) : (
								<Container>
									<FieldGrid>
										<div>
											<FormControl label={"First name"}>
												<Copy>{fields["Candidate.FirstName"]}</Copy>
											</FormControl>
										</div>
										<div>
											<FormControl label={"Middle name(s)"}>
												<Copy>{fields["Candidate.FirstMiddleNamesName"]}</Copy>
											</FormControl>
										</div>
										<div>
											<FormControl label={"Last name"}>
												<Copy>{fields["Candidate.LastName"]}</Copy>
											</FormControl>
										</div>
										<div></div>
										<div>
											<FormControl label={"Gender"}>
												<Copy>{fields["Candidate.Gender"]}</Copy>
											</FormControl>
										</div>
										<div>
											<FormControl label={"Date of Birth"}>
												<DateContainer>
													<Copy>
														{getDayPart(fields["Candidate.DateOfBirth"])}
													</Copy>
													<div className="separator">/</div>
													<Copy>
														{getMonthPart(fields["Candidate.DateOfBirth"])}
													</Copy>
													<div className="separator">/</div>
													<Copy>
														{getYearPart(fields["Candidate.DateOfBirth"])}
													</Copy>
												</DateContainer>
											</FormControl>
										</div>
										<div>
											<FormControl label={"Mobile"}>
												<Copy>{fields["Candidate.ContactMobile"]}</Copy>
											</FormControl>
										</div>
										<div>
											<FormControl label={"Phone"}>
												<Copy>{fields["Candidate.ContactPhone"]}</Copy>
											</FormControl>
										</div>
										<div>
											<FormControl label={"Email"}>
												<Copy>{fields["Candidate.ContactEmail"]}</Copy>
											</FormControl>
										</div>
										<div></div>
										<div>
											<FormControl label={"Physical address"}>
												<>
													<Copy>
														{fields["Candidate.PhysicalAddress"].address}
													</Copy>
													<br />
													<br />
													<Copy>
														{fields["Candidate.PhysicalAddress"].addressLine1}
													</Copy>
													{fields["Candidate.PhysicalAddress"].addressLine2 &&
														fields["Candidate.PhysicalAddress"].addressLine2 !==
															fields["Candidate.PhysicalAddress"]
																.addressLine1 && (
															<Copy>
																{
																	fields["Candidate.PhysicalAddress"]
																		.addressLine2
																}
															</Copy>
														)}
													{fields["Candidate.PhysicalAddress"].addressLine3 &&
														fields["Candidate.PhysicalAddress"].addressLine3 !==
															fields["Candidate.PhysicalAddress"]
																.addressLine2 && (
															<Copy>
																{
																	fields["Candidate.PhysicalAddress"]
																		.addressLine3
																}
															</Copy>
														)}
													{fields["Candidate.PhysicalAddress"].city &&
														fields["Candidate.PhysicalAddress"].city !==
															fields["Candidate.PhysicalAddress"]
																.addressLine3 && (
															<Copy>
																{fields["Candidate.PhysicalAddress"].city}
															</Copy>
														)}
													{fields["Candidate.PhysicalAddress"].region &&
														fields["Candidate.PhysicalAddress"].region !==
															fields["Candidate.PhysicalAddress"].city && (
															<Copy>
																{fields["Candidate.PhysicalAddress"].region}
															</Copy>
														)}
													<Copy>
														{fields["Candidate.PhysicalAddress"].postcode}
													</Copy>
													<Copy>
														{fields["Candidate.PhysicalAddress"].country}
													</Copy>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl label={"Mailing address"}>
												<>
													{fields["Candidate.PhysicalAddressSameAsMailing"] ? (
														<>Same as physical</>
													) : (
														<>
															<Copy>
																{fields["Candidate.MailingAddress"].address}
															</Copy>
															<br />
															<br />
															<Copy>
																{
																	fields["Candidate.MailingAddress"]
																		.addressLine1
																}
															</Copy>
															{fields["Candidate.MailingAddress"]
																.addressLine2 &&
																fields["Candidate.MailingAddress"]
																	.addressLine2 !==
																	fields["Candidate.MailingAddress"]
																		.addressLine1 && (
																	<Copy>
																		{
																			fields["Candidate.MailingAddress"]
																				.addressLine2
																		}
																	</Copy>
																)}
															{fields["Candidate.MailingAddress"]
																.addressLine3 &&
																fields["Candidate.MailingAddress"]
																	.addressLine3 !==
																	fields["Candidate.MailingAddress"]
																		.addressLine2 && (
																	<Copy>
																		{
																			fields["Candidate.MailingAddress"]
																				.addressLine3
																		}
																	</Copy>
																)}
															{fields["Candidate.MailingAddress"].city &&
																fields["Candidate.MailingAddress"].city !==
																	fields["Candidate.MailingAddress"]
																		.addressLine3 && (
																	<Copy>
																		{fields["Candidate.MailingAddress"].city}
																	</Copy>
																)}
															{fields["Candidate.MailingAddress"].region &&
																fields["Candidate.MailingAddress"].region !==
																	fields["Candidate.MailingAddress"].city && (
																	<Copy>
																		{fields["Candidate.MailingAddress"].region}
																	</Copy>
																)}
															<Copy>
																{fields["Candidate.MailingAddress"].postcode}
															</Copy>
															<Copy>
																{fields["Candidate.MailingAddress"].country}
															</Copy>
														</>
													)}
												</>
											</FormControl>
										</div>
									</FieldGrid>
								</Container>
							)}
						</Tab>
						{data.inZambion && (
							<Tab title="Tax Config">
								<Container>
									<FieldGrid>
										<div>
											<FormControl label="IRD Number">
												<>
													<Copy>{fields["CandidateDetail.IRDNumber"]}</Copy>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl label="Kiwisaver Member">
												<>
													{fields["CandidateDetail.KiwisaverMember"] ===
													true ? (
														<>Yes</>
													) : (
														<>No</>
													)}
												</>
											</FormControl>
										</div>
									</FieldGrid>
								</Container>
							</Tab>
						)}
						{data.inZambion && (
							<Tab title="Payroll: Payment">
								<Container>
									<FieldGrid>
										<div>
											<FormControl label="Payee Name">
												<>
													<Copy>
														{fields["CandidateDetail.BankAccountName"]}
													</Copy>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl label="Bank Account Number">
												<>
													<Copy>
														{fields["CandidateDetail.BankAccountNumber"]}
													</Copy>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl label="Bank">
												<>
													<Copy>
														{fields["CandidateDetail.BankAccountNumber"] &&
															fields["CandidateDetail.BankAccountNumber"].split(
																"-"
															)[0]}
													</Copy>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl label="Branch">
												<>
													<Copy>
														{fields["CandidateDetail.BankAccountNumber"] &&
															fields["CandidateDetail.BankAccountNumber"].split(
																"-"
															)[1]}
													</Copy>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl label="Account No">
												<>
													<Copy>
														{fields["CandidateDetail.BankAccountNumber"] &&
															fields["CandidateDetail.BankAccountNumber"].split(
																"-"
															)[2]}
													</Copy>
												</>
											</FormControl>
										</div>
										<div>
											<FormControl label="Suffix">
												<>
													<Copy>
														{fields["CandidateDetail.BankAccountNumber"] &&
															fields["CandidateDetail.BankAccountNumber"].split(
																"-"
															)[3]}
													</Copy>
												</>
											</FormControl>
										</div>
									</FieldGrid>
								</Container>
							</Tab>
						)}
						{data.inZambion && (
							<Tab title="Balance Tracking">
								<FieldGrid>
									<div>
										<FormControl label="Department">
											<>
												<Copy>
													{fields["CandidateInternal.Department"]}
												</Copy>
											</>
										</FormControl>
									</div>
									<div>
										<FormControl label="Std Days/Week">
											<>
												<Copy>
													{fields["CandidateAddendum.DaysOfWorkPerWeek"]}
												</Copy>
											</>
										</FormControl>
									</div>
									<div>
										<FormControl label="Manager">
											<>
												<Copy>{fields["CandidateAddendum.ManagerName"]}</Copy>
											</>
										</FormControl>
									</div>
									<div className="full-row">
										<FormControl label="Sites">
											<>
												<CandidateSites
													candidateSites={data.sites ?? []}
													showCopy={true}
												/>
											</>
										</FormControl>
									</div>
								</FieldGrid>
							</Tab>
						)}
						{data.inZambion && (
							<Tab title="Other">
								<FieldGrid>
									<div>
										<FormControl label="Next of Kin: Name">
											<>
												<Copy>{fields["Candidate.EmergencyContactName"]}</Copy>
											</>
										</FormControl>
									</div>
									<div>
										<FormControl label="Next of Kin: Mobile">
											<>
												<Copy>{fields["Candidate.EmergencyContactPhone"]}</Copy>
											</>
										</FormControl>
									</div>
									<div>
										<FormControl label="Next of Kin: Relationship">
											<>
												<Copy>
													{fields["Candidate.EmergencyContactRelationship"]}
												</Copy>
											</>
										</FormControl>
									</div>
								</FieldGrid>
							</Tab>
						)}
						{data.inZambion && (
							<Tab title="Custom">
								<FieldGrid>
									<div>
										<FormControl label="HR Covid Vaccination Status">
											<>
												<Copy>{fields["CandidateVaccine.COVID19"]}</Copy>
											</>
										</FormControl>
									</div>
									<div>
										<FormControl label="Ethnicity 1">
											<>
												<Copy>{fields["Candidate.Ethnicity"]}</Copy>
											</>
										</FormControl>
									</div>
									<div>
										<FormControl label="Citizen / Resident">
											<>
												<Copy>
													{fields["Candidate.CandidateResidencyStatus"] === 1 ||
													fields["Candidate.CandidateResidencyStatus"] === 2
														? "Yes"
														: "No"}
												</Copy>
											</>
										</FormControl>
									</div>

									<div>
										<FormControl label="Student Visa">
											<>
												<Copy>
													{fields["Candidate.CandidateResidencyStatus"] === 20
														? "Yes"
														: "No"}
												</Copy>
											</>
										</FormControl>
									</div>

									<div>
										<FormControl label="Work Visa">
											<>
												<Copy>
													{fields["Candidate.CandidateResidencyStatus"] ===
														10 ||
													fields["Candidate.CandidateResidencyStatus"] === 11
														? "Yes"
														: "No"}
												</Copy>
											</>
										</FormControl>
									</div>
									<div>
										<FormControl label="Visa Expiry Date">
											{fields["Candidate.VisaExpiry"] ? (
												<DateContainer>
													<Copy>
														{getDayPart(fields["Candidate.VisaExpiry"])}
													</Copy>
													<div className="separator">/</div>
													<Copy>
														{getMonthPart(fields["Candidate.VisaExpiry"])}
													</Copy>
													<div className="separator">/</div>
													<Copy>
														{getYearPart(fields["Candidate.VisaExpiry"])}
													</Copy>
												</DateContainer>
											) : (
												<></>
											)}
										</FormControl>
									</div>
								</FieldGrid>
							</Tab>
						)}
					</Tabs>
				</>
			)}
		</Container>
	);
};
export default CandidateZambion;
